import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { RouteComponentProps, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { animated } from "react-spring";
import { SyncOutlined } from "src/icons";
import styles from "./index.module.scss";
import PinnedContainer from "src/shared/components/ImCom/PinnedContainer";
import TopNavbar, { MenuItem } from "src/shared/components/Navbar/NewNavbar";
import { fetchHome, searchPin, searchReset, FetchHomePayload, SearchPinPayload } from "src/store/home";
import { fetchAllOrgMetrics, FetchAllOrgMetricsPayload } from "src/store/allOrg";
import { setCategoriesFilters, categoryReset } from "src/store/category";
import { setFilters, clearDetails } from "src/store/details";
import { changeTimeTabAppWide, UserRoles, setIsRoleCalloutShownAction } from "src/store/app";
import { logoutAction } from "src/store/login";
import RibbonSlider from "src/shared/components/RibbonSlider";
import { useRedux } from "src/shared/hooks";
import ManageMetrics from "./ManageMetric";
import Search from "src/shared/components/Search";
import { IRootState, IconClass } from "src/shared/types";
import { IReduxOperations, IActionFactory } from "src/store";
import { SearchTarget } from "src/shared/types";
import useDragger, { DRAGPANEL_TOP_THRESHOLD } from "./useDragger/index2";
import styled from "@emotion/styled";
import userService from "src/shared/services/user.service";
import colors from "src/styles/variables.scss";
import { useEffectOnPropsChange, checkIfIOSDevice, noop, safeGet } from "src/shared/utils";
import Permissions from "../../AllOrg/Permissions";
import { setComingBackFromDetailsPage } from "src/store/app";
import FilterByPod from "./FilterbyPod";
import { devLogging } from "src/utils/logger";
import { getCityOptions, getPodOptions } from "src/utils/getFilterOptions";
import { RunningAnobisMode } from "src/shared/config";
import { Button, message } from "antd";

const CALL_DELAY = 1000;

interface IState {
  metrics: IReduxOperations;
  search: IReduxOperations;
  selectedTab: any;
  userRoles: IReduxOperations<UserRoles>;
  isRoleCalloutShown: boolean;
}

type MappedActionProps = {
  fetchHome(payload: FetchHomePayload);
  searchPin(payload: SearchPinPayload);
  setCategoriesFilters: IActionFactory<symbol, IObject<any>>;
  categoryReset: IActionFactory<symbol, unknown>;
  searchReset: IActionFactory<symbol, unknown>;
  logoutAction();
  setFilters: IActionFactory<symbol, IObject<any>>;
  clearDetails: IActionFactory<symbol, any>;
  changeTimeTabAppWide: IActionFactory<symbol, string>;
  setIsRoleCalloutShown: IActionFactory<symbol, boolean>;
  fetchAllOrgMetrics(payload?: FetchAllOrgMetricsPayload);
  setComingBackFromDetailsPage: IActionFactory<symbol, boolean>;
};

const SliderContainer = styled.article`
  position: absolute;
  z-index: 1;
  width: inherit;
  height: 100vh;
  background: ${colors.blackBackground};
`;

const DragPanel = styled.section<{ translateYVal?: number }>`
  transition: ${checkIfIOSDevice() ? "none" : "transform 0.2s cubic-bezier(0.175, 0.985, 0.32, 1.275)"};
  -moz-transition: none;
`;

const SyncIcon = styled(SyncOutlined)`
  font-size: 1.5rem;
  height: min-content;
  width: 1.5rem;
  line-height: 15px;
`;

const ROLE_CALLOUT_SHOWN = "ROLE_CALLOUT_SHOWN";

const MainContainer: React.FC<RouteComponentProps> = ({ history }) => {
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/im-com-app", title: "IMCom Home" });
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, []);

  const [manageMetricsFlag, setManageMetricsFlag] = useState(false);
  const dragPanel = useRef<HTMLDivElement>(null as any);
  const allOrgElement = React.useRef<HTMLDivElement>(null as any);
  const [page, setPage] = useState(1);
  const [targetSearch, setTargetSearch] = useState(SearchTarget.HOME);
  const [showSearch, toggleSearch] = useState(false);
  const [timeTabForImCom, setTimeTabForImCom] = useState("LastHr_imcom");
  const containerRef = useRef<HTMLDivElement>(null as any);

  const [selectedPod, setSelectedPod] = useState<string[] | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(undefined);

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const store_id_filter_value = queryParams.get("store_id");
  const store_id_filter = store_id_filter_value
    ? [
        {
          field: "store_id",
          value: [store_id_filter_value],
        },
      ]
    : [];

  const showDetails = useCallback(
    (id, name) => {
      mappedProps.setComingBackFromDetailsPage(true);

      history.push(`/im-com-app/details/pin/${id}`);
      ReactGA.event({
        category: "Pin Clicked on L1a",
        label: `Pin: ${name} on L1a`,
        action: "Click from L1a",
      });
    },
    [history]
  );

  const [showRibbon, setShowRibbon] = useState(false);

  useDragger({
    dragPanel,
    onPanStart(e: HammerInput, isDockedUp: boolean) {
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "none";
        }
      }
    },
    onPanEnd(e: HammerInput, isDockedUp: boolean) {
      setShowRibbon(isDockedUp);
      if (isDockedUp) {
        document.body.style.overflowY = "hidden";
      } else {
        document.body.style.overflowY = "auto";
      }
      for (let childNode of dragPanel.current.childNodes) {
        if (childNode.nodeType === Node.ELEMENT_NODE) {
          (childNode as HTMLElement).style.pointerEvents = "initial";
        }
      }
    },
    shouldPerformTouchGesture({ target: touchedTargetEl, direction }: HammerInput) {
      const sensitiveContainerEl = allOrgElement.current;
      if (sensitiveContainerEl) {
        const isTragetInsideSensitiveZone = sensitiveContainerEl.contains(touchedTargetEl);
        return !isTragetInsideSensitiveZone;
      }
      return true;
    },
  });

  const {
    setFilters: setL3Fiters,
    clearDetails: clearL3Data,
    ...mappedProps
  } = useRedux<IRootState, IState, MappedActionProps, {}>(
    (state) => ({
      metrics: state.home.pinnedMetrics.metrics,
      search: state.home.pinnedMetrics.search,
      loadMorePending: state.home.pinnedMetrics.loadMorePending,
      chartsDataPending: state.home.pinnedMetrics.chartsDataPending,
      selectedTab: state.app.selectedTab,
      comingBackFromDetailsPage: state.app.comingBackFromDetailsPage,
      userRoles: state.app.userRoles,
      isRoleCalloutShown: state.app.isRoleCalloutShown,
    }),
    {
      fetchHome,
      searchPin,
      searchReset,
      logoutAction,
      setCategoriesFilters,
      categoryReset,
      setFilters,
      clearDetails,
      changeTimeTabAppWide,
      setIsRoleCalloutShown: setIsRoleCalloutShownAction,
      fetchAllOrgMetrics,
      setComingBackFromDetailsPage,
    }
  );

  const onLogout = useCallback(() => {
    userService.logout();
    history.push("/");
    mappedProps.logoutAction();
  }, []);

  const [isRefreshClicked, setIsRefreshClicked] = useState(0);
  const [shouldShowPermissions, setShouldShowPermissions] = useState(false);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [visibleMetrics, setVisibleMetrics] = useState(mappedProps.metrics);
  const [podOptionsState, setPodOptions] = useState([]);

  useEffect(() => {
    setVisibleMetrics(mappedProps.metrics);
  }, [mappedProps.metrics]);

  useEffect(() => {
    const fetchPodOptions = async () => {
      // @ts-ignore
      const options = await getPodOptions(mappedProps.userRoles?.payload, selectedCity);
      setPodOptions(options);
    };
    fetchPodOptions();
  }, [mappedProps.userRoles?.payload?.pods, mappedProps.userRoles?.payload?.podNames, selectedCity]);

  // persist the filters on refresh
  const onRefresh = useCallback(() => {
    setIsRefreshClicked(Date.now());

    const currentFilters = [
      ...(selectedCity ? [{ field: "city", value: [selectedCity] }] : []),
      {
        field: "store_id",
        value: selectedPod && selectedPod.length > 0 ? selectedPod : mappedProps.userRoles.payload?.pods || [],
      },
    ];

    setTimeout(() => {
      setPage(1);
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters: currentFilters });
      mappedProps.fetchAllOrgMetrics({ fresh: true });

      const selectedPodFilter = currentFilters.find((filter) => filter.field === "store_id");
      const selectedCityFilter = currentFilters.find((filter) => filter.field === "city");
      const selectedMetricsFilter = currentFilters.find((filter) => filter.field === "metrics");

      setSelectedPod(selectedPodFilter ? selectedPodFilter.value : []);
      setSelectedCity(selectedCityFilter ? selectedCityFilter.value[0] : undefined);
    }, CALL_DELAY);
  }, [selectedPod, selectedCity, mappedProps]);

  //@ts-ignore
  const cityOptions = useMemo(
    //@ts-ignore
    () => getCityOptions(mappedProps.userRoles?.payload),
    [mappedProps.userRoles?.payload?.cities]
  );

  const handlePodChange = useCallback(
    (value: string[]) => {
      setSelectedPod(value);

      devLogging("selected values", value, selectedCity);

      const filters = [
        ...(selectedCity ? [{ field: "city", value: [selectedCity] }] : []),
        {
          field: "store_id",
          value: value.length > 0 ? value : mappedProps.userRoles.payload?.pods || [],
        },
      ];
      mappedProps.fetchHome({ pageNo: 1, fresh: true, filters });
      setPage(1);
    },
    [
      selectedPod,
      selectedCity,
      mappedProps.fetchHome,
      mappedProps.userRoles.payload?.pods,
      mappedProps.userRoles.payload?.cities,
    ]
  );

  const handleCityChange = useCallback(
    (value: string) => {
      setSelectedCity(value);
      // on city change, reset the selected pod
      setSelectedPod([]);
    },
    [selectedCity, mappedProps.userRoles.payload?.pods, mappedProps.userRoles.payload?.cities]
  );

  useEffect(() => {
    if (
      isRefreshClicked &&
      Date.now() - isRefreshClicked > CALL_DELAY &&
      safeGet(mappedProps, "chartsDataPending") === false
    ) {
      setIsRefreshClicked(0);
    }
  }, [safeGet(mappedProps, "chartsDataPending"), isRefreshClicked]);

  const onSearchClick = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(true);
  }, []);

  const closeSearch = useCallback(() => {
    mappedProps.searchReset();
    toggleSearch(false);
    setTargetSearch(SearchTarget.HOME);
  }, []);

  const handleChangeTab = useCallback(
    ({ label }: { label: string }) => {
      setPage(1);
      mappedProps.changeTimeTabAppWide(label);
      setTimeTabForImCom(label);
    },
    [mappedProps]
  );

  // persist the selected tab
  useEffect(() => {
    const savedTab = mappedProps.selectedTab;
    devLogging("saved a tab from the world ", savedTab);
    if (RunningAnobisMode.SHOW_IM_COM_TIME_TAB.includes(savedTab)) {
      // if selectedTab is in the list of time tabs for IMCOM
      setTimeTabForImCom(savedTab);
      mappedProps.changeTimeTabAppWide(savedTab);
    } else {
      mappedProps.changeTimeTabAppWide(timeTabForImCom);
      handleChangeTab({ label: timeTabForImCom }); // set the default tab
    }
  }, [timeTabForImCom, mappedProps]);

  useEffectOnPropsChange(() => {
    devLogging("selected tab changed and fetch home getting called without anyfilter", mappedProps.selectedTab);
    const filters = [
      ...(selectedCity ? [{ field: "city", value: [selectedCity] }] : []),
      {
        field: "store_id",
        value: selectedPod && selectedPod.length > 0 ? selectedPod : mappedProps.userRoles.payload?.pods || [],
      },
    ];
    mappedProps.fetchHome({ pageNo: 1, fresh: true, filters });
  }, [mappedProps.selectedTab]);

  useEffect(() => {
    if (mappedProps?.metrics?.errors) {
      message.error(mappedProps?.metrics?.errors);
    }
  }, [mappedProps]);

  const loadMore = useCallback(() => {
    if (
      !(safeGet(mappedProps, "metrics.pending", false) || safeGet(mappedProps, "loadMorePending", false)) &&
      safeGet(mappedProps, "metrics.payload.hasMore", false)
    ) {
      const filters = [
        ...(selectedCity ? [{ field: "city", value: [selectedCity] }] : []),
        {
          field: "store_id",
          value: selectedPod && selectedPod.length > 0 ? selectedPod : mappedProps.userRoles.payload?.pods || [],
        },
      ];
      mappedProps.fetchHome({ pageNo: page + 1, filters: filters });
      setPage(page + 1);
    }
  }, [mappedProps.metrics, safeGet(mappedProps, "loadMorePending"), page]);

  useEffect(() => {
    if (safeGet(mappedProps, "comingBackFromDetailsPage") !== true) {
      mappedProps.fetchHome({ pageNo: 1, fresh: true });
      mappedProps.fetchAllOrgMetrics({ fresh: true });
      mappedProps.setCategoriesFilters({});
      mappedProps.categoryReset();
      setL3Fiters({});
      clearL3Data();
    } else {
      onRefresh();
      mappedProps.setComingBackFromDetailsPage(false);
      if (safeGet(mappedProps, "metrics.success") === false) {
        mappedProps.fetchHome({ pageNo: 1, fresh: true });
        mappedProps.fetchAllOrgMetrics({ fresh: true });
      }
    }

    setSelectedPod([]);
    setSelectedCity(undefined);
    setPage(1);
  }, []);

  useEffect(() => {
    const currentDiv = containerRef.current;
    currentDiv?.addEventListener("scroll", () => {
      localStorage.setItem("AnobisYscrollPositionPinnedPage", currentDiv?.scrollTop.toString());
    });
    if (mappedProps.metrics.success) {
      const scrollPosition: any = localStorage.getItem("AnobisYscrollPositionPinnedPage");
      currentDiv?.scrollTo({
        top: parseInt(scrollPosition),
        behavior: "smooth",
      });
    }
  });

  // set the default city if there is only one city
  useEffect(() => {
    if (cityOptions.length === 1) {
      setSelectedCity(cityOptions[0].value);
      handleCityChange(cityOptions[0].value);
    }
  }, [cityOptions, mappedProps.selectedTab]);

  const options = useMemo(
    () => [
      {
        className: "",
        iconClass: IconClass.SEARCH,
        handleClick: onSearchClick,
      },
      {
        className: "",
        iconClass: IconClass.COMPARE,
        handleClick: () => history.push("/metric-comparison"),
      },
    ],
    [onSearchClick]
  );

  const searchMetric = (text) => {
    mappedProps.searchPin({ text, targetSearch });
  };

  const meticOptionsV1 = useMemo(() => {
    return mappedProps.metrics.payload?.data.map((metric) => ({
      value: metric.label,
      label: metric.label,
    }));
  }, [mappedProps.metrics]);

  return (
    <>
      {!manageMetricsFlag && (
        <animated.section
          className={`${styles["container"]} ${showRibbon ? styles.overlayOpen : ""} ${
            showSearch ? styles["display-none"] : ""
          }`}
          style={{ position: "absolute" }}
          ref={(target) => {
            containerRef.current = target as HTMLDivElement;
          }}
        >
          {showRibbon && safeGet(mappedProps, "metrics.success", false) ? (
            <SliderContainer>
              <RibbonSlider height={DRAGPANEL_TOP_THRESHOLD} pins={safeGet(mappedProps, "metrics.payload", [])} />
            </SliderContainer>
          ) : null}
          <div className={styles["home-top-nav"]} style={{ opacity: showRibbon ? 0 : 1 }}>
            <TopNavbar
              handleClick={noop}
              headerText=""
              isHome
              onLogout={onLogout}
              onPermissionsShow={() => setShouldShowPermissions(true)}
            >
              <>
                {safeGet(mappedProps, "chartsDataPending", false) || !!isRefreshClicked ? (
                  <SyncIcon spin style={{ color: "blue" }} />
                ) : (
                  <SyncIcon onClick={onRefresh} style={{ color: "black" }} />
                )}

                {store_id_filter.length == 0 && meticOptionsV1 && (
                  <FilterByPod
                    selectedPod={selectedPod || undefined}
                    handlePodChange={handlePodChange}
                    podOptions={podOptionsState}
                    cityOptions={cityOptions}
                    selectedCity={selectedCity}
                    handleSelectedCity={(value) => {
                      setSelectedPod([]);
                      setSelectedCity(value);
                      handleCityChange(value);
                    }}
                    filterType={filterType}
                    setFilterType={setFilterType}
                  />
                )}
              </>
            </TopNavbar>
          </div>
          {(!mappedProps?.metrics?.errors && (
            <>
              <div className={styles["pinned-items"]} style={{ opacity: showRibbon ? 0 : 1 }}>
                <PinnedContainer
                  isPulledUp={showRibbon}
                  pins={visibleMetrics}
                  showDetails={showDetails}
                  isPending={safeGet(mappedProps, "metrics.pending", false)}
                  onChangeTab={handleChangeTab}
                  switchToManageMetrics={setManageMetricsFlag}
                  selectedTab={mappedProps.selectedTab}
                  brokenPodsVisibleTabs={RunningAnobisMode.BROKEN_PODS_VISIBLE_TIME_TAB}
                />
              </div>
              {safeGet(mappedProps, "metrics.payload.hasMore", false) && (
                <Button type="primary" shape="round" className={styles["load-more-button"]} onClick={loadMore}>
                  Load more
                </Button>
              )}
            </>
          )) || (
            <div className={styles.errorContainer}>
              <div className={styles.errorMessage}>{mappedProps?.metrics?.errors}</div>
            </div>
          )}
        </animated.section>
      )}
      {manageMetricsFlag && (
        <>
          <ManageMetrics refreshHome={onRefresh} switchBackToHome={setManageMetricsFlag} />
        </>
      )}
      {showSearch && (
        <Search
          closeSearch={closeSearch}
          searchMetric={searchMetric}
          searchResult={safeGet(mappedProps, "search.payload", []) || []}
          open={showSearch}
          target={targetSearch}
          searchSuccess={safeGet(mappedProps, "search.success", false)}
          searchPending={safeGet(mappedProps, "search.pending", false)}
          resetSearch={mappedProps.searchReset}
        />
      )}
      {shouldShowPermissions && (
        <Permissions roles={mappedProps.userRoles} onClose={() => setShouldShowPermissions(false)} />
      )}
    </>
  );
};

export default MainContainer;
